import { ColumnDef, Table } from '@tanstack/react-table';
import { Icon, Tooltip, TooltipTrigger } from '@toggle/design-system';
import { ColumnMeta, formatTicker } from '@toggle/toggle';
import { TFunction } from 'i18next';
import React from 'react';

import { TruncateTooltip } from '~/components/truncate-tooltip/TruncateTooltip';
import { globalPriceFormatter } from '~/utils/asset-price/assetPrice';
import { mapColumnMeta } from '~/views/screener/results/screener-table-utils';

import * as S from './AssetsTable.styles';
import { InsightsCell } from './insights-cell/InsightsCell';
import { EntityPlusPrice } from './useAssetsTableData';

export const INSIGHTS_COL_ID = 'insights1W';

type AssetsTableColumnsProps = {
  t: TFunction;
  selectedInsight: string | undefined;
  setSelectedInsight: (insightId: string) => void;
  setInsightsDrawerOpen: (open: boolean) => void;
  insightsDrawerOpen: boolean;
  columnsMeta: ColumnMeta[];
  tab: string;
  isEditMode: boolean;
  toggleAllRowsSelection: (selected: boolean) => void;
};

export const getColumns = ({
  t,
  selectedInsight,
  setSelectedInsight,
  setInsightsDrawerOpen,
  insightsDrawerOpen,
  columnsMeta,
  tab,
  isEditMode,
  toggleAllRowsSelection,
}: AssetsTableColumnsProps) => {
  const editColumn = isEditMode
    ? [
        {
          id: 'rowSelected',
          header: ({ table }: { table: Table<EntityPlusPrice> }) => (
            <S.CheckboxWrapper>
              <S.StyledCheckBox
                name="watchlist-select"
                label=""
                onChange={(_, e) => toggleAllRowsSelection(e.target.checked)}
                checked={table.getIsAllRowsSelected()}
              />
            </S.CheckboxWrapper>
          ),
          cell: () => <span />,
          size: 52,
        },
      ]
    : [];

  const defaultColumns: ColumnDef<EntityPlusPrice>[] = [
    ...editColumn,
    {
      id: 'entity',
      header: t('widget:watchlist.ticker'),
      cell: props => {
        const item = props.row.original;
        return item.asset ? (
          <S.TickerCell>
            <S.StyledAssetLogoContainer entity={item.asset} size={24} />
            <S.Ticker>{formatTicker(item.asset)}</S.Ticker>
          </S.TickerCell>
        ) : null;
      },
      size: 150,
    },
    {
      id: 'name',
      header: t('widget:watchlist.name'),
      minSize: 90,
      cell: props => {
        const item = props.row.original;
        return (
          <TruncateTooltip
            inPortal
            atLength={20}
            text={item.asset?.name ?? ''}
          />
        );
      },
    },
  ];

  const mappedColumns = columnsMeta.map<ColumnDef<EntityPlusPrice>>(cm =>
    mapColumnMeta(cm, isEditMode)
  );

  if (tab === 'Overview') {
    const columns: ColumnDef<EntityPlusPrice>[] = [
      ...defaultColumns,
      {
        id: 'price',
        header: t('widget:watchlist.price'),
        cell: props => {
          const { asset, price, stamp } = props.row.original;
          return (
            <S.FlexWrapper>
              {stamp && (
                <Tooltip
                  isTouchDevice={false}
                  trigger={TooltipTrigger.Hover}
                  label={
                    /* i18next-extract-mark-context-next-line ["asOfLastClose", "isAfterMarket", "isMarketHours", "isPreMarket", "livePrice"] */
                    t('watchlistManager:tooltips.tooltip', {
                      context: stamp.tooltipKey,
                    })
                  }
                >
                  <S.StampWrapper
                    $color={isEditMode ? 'var(--icon-disabled)' : stamp.color}
                  >
                    <Icon iconName={stamp.iconName} size={12} />
                  </S.StampWrapper>
                </Tooltip>
              )}
              {asset && (
                <S.PriceWrapper $isDisabled={isEditMode}>
                  <span>
                    {globalPriceFormatter({
                      price: price,
                      entity: asset,
                      priceSuffix: '',
                    })}
                  </span>
                  <S.Currency>{asset.currency}</S.Currency>
                </S.PriceWrapper>
              )}
            </S.FlexWrapper>
          );
        },
        accessorKey: 'price',
        sortingFn: 'basic',
      },
      ...mappedColumns,
      {
        id: INSIGHTS_COL_ID,
        header: t('widget:watchlist.insights1W'),
        cell: props => (
          <InsightsCell
            entityTag={props.row.original.tag}
            selectedInsight={selectedInsight}
            setSelectedInsight={setSelectedInsight}
            setInsightsDrawerOpen={setInsightsDrawerOpen}
            insightsDrawerOpen={insightsDrawerOpen}
            isDisabled={isEditMode}
          />
        ),
        enableSorting: false,
      },
    ];
    return columns;
  }

  return [...defaultColumns, ...mappedColumns];
};
